import React, { useState } from 'react';
import { Box, Dialog, Divider, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ModalCard from '../../ui/ModalCard';
import { XIcon } from '../../icons';
import { createChild, ViolationField } from '../../../api/childrenApi';
import { calculateAge } from '../../../utils/calendar';
import { Step1, type FormState, type FieldErrors } from './AddChildStep1';
import { Step2 } from './AddChildStep2';

interface AddChildModalProps {
  open: boolean;
  onClose: () => void;
  onChildAdded: (fullName: string) => void;
}

const VIOLATION_FIELD_MAP: Partial<Record<ViolationField, keyof FieldErrors>> = {
  FIRST_NAME: 'firstName',
  LAST_NAME: 'lastName',
  BIRTH_DATE: 'birthDate',
  SEX: 'sex',
};

function getInitials(firstName: string, lastName: string): string {
  return `${firstName[0] ?? ''}${lastName[0] ?? ''}`.toUpperCase();
}

function parseBirthDate(raw: string): Date | null {
  const match = raw.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (!match) return null;
  const d = new Date(+match[3], +match[2] - 1, +match[1]);
  return isNaN(d.getTime()) ? null : d;
}

function toApiDate(date: Date): string {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function formatBirthDate(date: Date): string {
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  return `${dd}.${mm}.${date.getFullYear()}`;
}

export default function AddChildModal({ open, onClose, onChildAdded }: AddChildModalProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState<1 | 2>(1);
  const [form, setForm] = useState<FormState>({ firstName: '', lastName: '', birthDate: '', sex: null });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);

  function handleClose() {
    onClose();
    setTimeout(() => {
      setStep(1);
      setForm({ firstName: '', lastName: '', birthDate: '', sex: null });
      setErrors({});
    }, 300);
  }

  function handleNext() {
    const parsedDate = parseBirthDate(form.birthDate);
    const newErrors: FieldErrors = {
      firstName: !form.firstName.trim(),
      lastName: !form.lastName.trim(),
      birthDate: !parsedDate,
      sex: form.sex === null,
    };
    setErrors(newErrors);
    if (Object.values(newErrors).some(Boolean)) return;
    setStep(2);
  }

  async function handleSubmit() {
    const parsedDate = parseBirthDate(form.birthDate);
    if (!parsedDate || !form.sex) return;
    setSubmitting(true);
    try {
      const result = await createChild({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        birthDate: toApiDate(parsedDate),
        sex: form.sex,
      });
      if (result.isSuccess) {
        onChildAdded(`${form.firstName.trim()} ${form.lastName.trim()}`);
        handleClose();
      } else if (result.error) {
        const newErrors: FieldErrors = {};
        for (const v of result.error.violations) {
          const field = VIOLATION_FIELD_MAP[v.details.field];
          if (field) newErrors[field] = true;
        }
        setErrors(newErrors);
        setStep(1);
      }
    } catch {
      // TODO(backend): show network error toast
    } finally {
      setSubmitting(false);
    }
  }

  const parsedDate = parseBirthDate(form.birthDate);
  const initials = getInitials(form.firstName, form.lastName);
  const age = parsedDate ? calculateAge(parsedDate) : null;
  const birthDateFormatted = parsedDate ? formatBirthDate(parsedDate) : form.birthDate;

  const modalHeader = (
    <Box sx={{ mt: -5, mx: -5, mb: -1 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 5,
          py: 2,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {t('addChild.modalTitle')}{' '}
          <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}>
            {step}/2
          </Box>
        </Typography>
        <IconButton onClick={handleClose} size="small" sx={{ color: 'text.secondary' }}>
          <XIcon size={16} />
        </IconButton>
      </Box>
      <Divider />
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      slotProps={{
        paper: {
          elevation: 0,
          sx: { backgroundColor: 'transparent', overflow: 'visible', m: 2 },
        },
      }}
    >
      <ModalCard dialog>
        {step === 1 ? (
          <Step1
            header={modalHeader}
            form={form}
            errors={errors}
            onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
            onNext={handleNext}
          />
        ) : (
          <Step2
            header={modalHeader}
            firstName={form.firstName.trim()}
            lastName={form.lastName.trim()}
            sex={form.sex!}
            initials={initials}
            age={age}
            birthDateFormatted={birthDateFormatted}
            submitting={submitting}
            onBack={() => setStep(1)}
            onSubmit={handleSubmit}
          />
        )}
      </ModalCard>
    </Dialog>
  );
}
